// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  assetAccountingPropertiesSchema,
  sparseAssetSchema,
} from '@/asset/api/types';
import { exceptionTypeSchema } from '@/constants/asset-exception-type';
import { assetFunctionalStatusSchema } from '@/constants/asset-functional-status';
import { orgDefaultCurrencyCodeSchema } from '@/constants/currency';
import { ProjectCompanyType } from '@/constants/project-company-type';
import {
  ProjectStatusSlug,
  projectStatusSchema,
} from '@/constants/project-status';
import { Sort } from '@/constants/sort';
import { normalizedIso } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { leftInventoryForSchema } from '@/report/types';
import {
  projectIdentifierSchema,
  sparseCustomerSchema,
  sparseItemSchema,
  sparseLocationWithParentSchema,
} from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { decimalDaysSchema } from '@/utilities/decimal-days-schema';
import type { Interval } from 'luxon';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Schema for one cycle-time metric segment shared by the fulfillment and
 * shipment cycle-time reports.
 */
export const cycleTimeMetricSchema = z.object({
  p10Days: decimalDaysSchema,
  p25Days: decimalDaysSchema,
  medianDays: decimalDaysSchema,
  avgDays: decimalDaysSchema,
  p75Days: decimalDaysSchema,
  p90Days: decimalDaysSchema,
  count: z.number(),
});

export type CycleTimeMetric = z.infer<typeof cycleTimeMetricSchema>;

export interface CycleTimeReportFilter {
  interval: Interval;
}

export const fulfillmentBySkuReportStatusSchema = z.enum([
  'OPEN',
  'PARTIALLY_FULFILLED',
  'FULLY_FULFILLED',
  'OVERAGE',
]);

export type FulfillmentBySkuReportStatus = z.infer<
  typeof fulfillmentBySkuReportStatusSchema
>;

export const FULFILLMENT_BY_SKU_REPORT_STATUSES =
  fulfillmentBySkuReportStatusSchema.options;

export const DEFAULT_FULFILLMENT_BY_SKU_REPORT_STATUSES = [
  'OPEN',
  'PARTIALLY_FULFILLED',
] as const satisfies readonly FulfillmentBySkuReportStatus[];

export const FULFILLMENT_BY_SKU_REPORT_STATUS_LABELS: Record<
  FulfillmentBySkuReportStatus,
  string
> = {
  OPEN: 'Open',
  PARTIALLY_FULFILLED: 'Partially fulfilled',
  FULLY_FULFILLED: 'Fully fulfilled',
  OVERAGE: 'Overage',
};

export const fulfillmentBySkuReportSortPropertySchema = z.enum([
  'company',
  'project',
  'sku',
  'item',
  'status',
  'demandStart',
  'demandEnd',
  'projectRequested',
  'foRequested',
  'pending',
  'shipped',
  'delivered',
  'netNeedQuantity',
]);

export type FulfillmentBySkuReportSortProperty = z.infer<
  typeof fulfillmentBySkuReportSortPropertySchema
>;

const fulfillmentBySkuReportProjectSchema = projectIdentifierSchema.extend({
  status: projectStatusSchema,
});

export const fulfillmentBySkuReportRowSchema = z.object({
  company: sparseCustomerSchema.nullable(),
  project: fulfillmentBySkuReportProjectSchema,
  item: sparseItemSchema,
  status: fulfillmentBySkuReportStatusSchema,
  demandStart: dateSchema.nullable(),
  demandEnd: dateSchema.nullable(),
  projectRequested: z.number(),
  foRequested: z.number(),
  pending: z.number(),
  shipped: z.number(),
  delivered: z.number(),
  netNeedQuantity: z.number(),
});

export type FulfillmentBySkuReportRow = z.infer<
  typeof fulfillmentBySkuReportRowSchema
>;

export interface FulfillmentBySkuReportFilter {
  statuses?: FulfillmentBySkuReportStatus[];
  projectIds?: string[];
  projectStatuses?: ProjectStatusSlug[];
  companyTypes?: ProjectCompanyType[];
  companyIds?: string[];
  itemIds?: string[];
  needByFrom?: DateTime | null;
  needByTo?: DateTime | null;
  pageSize?: number;
  pageNumber?: number;
  sort?: Sort<FulfillmentBySkuReportSortProperty>;
}

export const assetLocationReportRowSchema = z.object({
  id: z.string(),
  serial: z.string(),
  productName: z.string(),
  productSku: z.string(),
  locationName: z.string(),
  presenceBitMask: z.string(),
});

export type AssetLocationReportRow = z.infer<
  typeof assetLocationReportRowSchema
>;

export const assetTransitReportRowSchema = z.object({
  id: z.string(),
  serial: z.string(),
  productName: z.string(),
  productSku: z.string(),
  presenceBitMask: z.string(),
});

export type AssetTransitReportRow = z.infer<typeof assetTransitReportRowSchema>;

/**
 * The envelope the three presence reports return. The window is what the bit masks on each
 * row are read against, described in ai/context/backend/presence-bit-mask.md.
 */
export interface PresenceReportResponse<TRow> {
  startOn: string;
  dayCount: number;
  data: TRow[];
}

export const projectReportRowSchema = z.object({
  id: z.string(),
  projectName: z.string(),
  productName: z.string(),
  customerName: z.string().nullable(),
  cells: z.array(
    z.object({ date: z.string(), requestedUnitCount: z.number().nullable() })
  ),
});

export type ProjectReportRow = z.infer<typeof projectReportRowSchema>;

export const inventoryAssetSnapshotItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
  description: z.string().nullable(),
  functionalStatus: assetFunctionalStatusSchema,
  item: sparseItemSchema,
  location: sparseLocationWithParentSchema.nullable(),
  inTransit: z.boolean(),
  inventoryDate: dateSchema,
  daysInInventory: z.number().nullable(),
  accounting: assetAccountingPropertiesSchema,
  expectedDepreciableAmount: z.string().nullable(),
});

export type InventoryAssetSnapshotItem = z.infer<
  typeof inventoryAssetSnapshotItemSchema
>;

export const fixedAssetSnapshotItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
  functionalStatus: assetFunctionalStatusSchema,
  description: z.string().nullable(),
  inTransit: z.boolean(),
  item: sparseItemSchema,
  location: sparseLocationWithParentSchema.nullable(),
  accounting: assetAccountingPropertiesSchema,
  inServiceDate: dateSchema.nullable(),
  endOfUsefulLife: dateSchema.nullable(),
  daysOfUsefulLife: z.number().nullable(),
  depreciableAmount: z.string().nullable(),
  daysInService: z.number().nullable(),
  accumulatedDepreciation: z.string().nullable(),
  bookValue: z.string().nullable(),
});

export type FixedAssetSnapshotItem = z.infer<
  typeof fixedAssetSnapshotItemSchema
>;

const depreciationScheduleCellSchema = z.object({
  periodDepreciation: z.string().nullable(),
  accumulatedDepreciation: z.string().nullable(),
  netBookValue: z.string().nullable(),
});

export const depreciationScheduleDisplayModes = [
  'periodDepreciation',
  'accumulatedDepreciation',
  'netBookValue',
] as const;

export type DepreciationScheduleDisplayMode =
  (typeof depreciationScheduleDisplayModes)[number];

export const depreciationSchedulePeriodSchema = z.object({
  startDate: dateSchema,
  endDate: dateSchema,
  label: z.string(),
});

export type DepreciationSchedulePeriod = z.infer<
  typeof depreciationSchedulePeriodSchema
>;

export const depreciationScheduleAssetSchema = sparseAssetSchema.extend({
  currentLocation: sparseLocationWithParentSchema.nullable(),
  accounting: assetAccountingPropertiesSchema,
  cells: z.array(depreciationScheduleCellSchema),
});

export type DepreciationScheduleAsset = z.infer<
  typeof depreciationScheduleAssetSchema
>;

export const depreciationScheduleReportSchema = z.object({
  periods: z.array(depreciationSchedulePeriodSchema),
  assets: z.array(depreciationScheduleAssetSchema),
});

export type DepreciationScheduleReport = z.infer<
  typeof depreciationScheduleReportSchema
>;

export const depreciationScheduleReportEstimateSchema = z.object({
  assetCount: z.number().int().nonnegative(),
});

export type DepreciationScheduleReportEstimate = z.infer<
  typeof depreciationScheduleReportEstimateSchema
>;

export const soldAssetSnapshotItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
  description: z.string().nullable(),
  inTransit: z.boolean(),
  functionalStatus: assetFunctionalStatusSchema,
  item: sparseItemSchema,
  location: sparseLocationWithParentSchema.nullable(),
  accounting: assetAccountingPropertiesSchema,
  inventoryDate: dateSchema,
  daysInInventory: z.number().nullable(),
  leftInventoryFor: leftInventoryForSchema,
  daysInServiceOnBooks: z.number().nullable(),
  endOfUsefulLife: dateSchema.nullable(),
  daysOfUsefulLife: z.number().nullable(),
  saleDate: dateSchema.nullable(),
  daysInServiceOffBooks: z.number().nullable(),
  depreciableAmount: z.string().nullable(),
  accumulatedDepreciation: z.string().nullable(),
  bookValueAtSale: z.string().nullable(),
  saleAmount: z.string().nullable(),
  saleCustomer: sparseCustomerSchema.nullable(),
  saleGainLoss: z.string().nullable(),
  scrapDate: dateSchema.nullable(),
  scrapReason: z.string().nullable(),
});

export type SoldAssetSnapshotItem = z.infer<typeof soldAssetSnapshotItemSchema>;

export const scrappedAssetSnapshotItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
  description: z.string().nullable(),
  inTransit: z.boolean(),
  functionalStatus: assetFunctionalStatusSchema,
  item: sparseItemSchema,
  location: sparseLocationWithParentSchema.nullable(),
  accounting: assetAccountingPropertiesSchema,
  inventoryDate: dateSchema,
  daysInInventory: z.number().nullable(),
  leftInventoryFor: leftInventoryForSchema,
  daysInService: z.number().nullable(),
  endOfUsefulLife: dateSchema.nullable(),
  daysOfUsefulLife: z.number().nullable(),
  depreciableAmount: z.string().nullable(),
  accumulatedDepreciation: z.string().nullable(),
  bookValueAtScrap: z.string().nullable(),
  scrapDate: dateSchema,
  scrapAmount: z.string().nullable(),
  scrapReason: z.string().nullable(),
  disposalGainLoss: z.string().nullable(),
});

export type ScrappedAssetSnapshotItem = z.infer<
  typeof scrappedAssetSnapshotItemSchema
>;

export const rollforwardAssetSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
  item: sparseItemSchema,
  functionalStatus: assetFunctionalStatusSchema,
  capitalizedCosts: z.string().nullable(),
  accumulatedDepreciation: z.string().nullable(),
  bookValue: z.string().nullable(),
});

const assetPhaseDataSchema = z.object({
  units: z.number(),
  dollars: z.string(),
  assets: z.array(rollforwardAssetSchema),
});

export const inventoryRollforwardDataSchema = z.object({
  start: assetPhaseDataSchema,
  added: assetPhaseDataSchema,
  deployed: assetPhaseDataSchema,
  sold: assetPhaseDataSchema,
  scrapped: assetPhaseDataSchema,
  end: assetPhaseDataSchema,
});

export type InventoryRollforwardData = z.infer<
  typeof inventoryRollforwardDataSchema
>;

export const fixedAssetsRollforwardDataSchema = z.object({
  start: assetPhaseDataSchema,
  added: assetPhaseDataSchema,
  depreciated: assetPhaseDataSchema,
  sold: assetPhaseDataSchema,
  scrapped: assetPhaseDataSchema,
  end: assetPhaseDataSchema,
});

export type FixedAssetsRollforwardData = z.infer<
  typeof fixedAssetsRollforwardDataSchema
>;

const inventoryBridgePeriodSchema = z.object({
  periodStart: dateSchema,
  periodEnd: dateSchema,
  rollforward: inventoryRollforwardDataSchema,
});

export const inventoryBridgeDataSchema = z.object({
  // The single cost currency every period is denominated in, so the chart labels money
  // from the response rather than from a client-side guess.
  isoCode: normalizedIso(isoCurrencyCodeSchema),
  interval: z.string(),
  periods: z.array(inventoryBridgePeriodSchema),
});

export type InventoryBridgeData = z.infer<typeof inventoryBridgeDataSchema>;

export const dateInconsistencyItemSchema = z.object({
  asset: sparseAssetSchema,
  inventoryDate: dateSchema,
  inServiceDate: dateSchema.nullable(),
  scrapDate: dateSchema.nullable(),
  firstSaleDate: dateSchema.nullable(),
  firstAssignmentDate: dateSchema.nullable(),
  firstMovementDate: dateSchema.nullable(),
  exceptionTypes: z.array(exceptionTypeSchema),
});

export type DateInconsistencyItem = z.infer<typeof dateInconsistencyItemSchema>;

export const detailInconsistencyItemSchema = z.object({
  asset: sparseAssetSchema,
  inServiceDate: dateSchema.nullable(),
  usefulLife: z.number().nullable(),
  inventoryCostBasis: z.string().nullable(),
  deployedCostBasis: z.string().nullable(),
  salvageValue: z.string().nullable(),
  // Required on the asset and only ever written from the app-allowed set, so the schema
  // says so: a wider type would relabel an anomalous code as the org default rather than
  // failing, and the report would assert the wrong denomination.
  costCurrencyCode: normalizedIso(orgDefaultCurrencyCodeSchema),
  exceptionTypes: z.array(exceptionTypeSchema),
});

export type DetailInconsistencyItem = z.infer<
  typeof detailInconsistencyItemSchema
>;

/**
 * Schema for validating date exception form data.
 * Keeps editable date picker fields as DateTime values in form state.
 */
export const dateExceptionFormSchema = z.object({
  inventoryDate: dateSchema.nullable(),
  inServiceDate: dateSchema.nullable(),
  scrapDate: dateSchema.nullable(),
  firstSaleDate: dateSchema.nullable(),
  firstAssignmentDate: dateSchema.nullable(),
  firstMovementDate: dateSchema.nullable(),
});

/**
 * Form data structure representing all fields that can be changed
 * when resolving date exceptions.
 */
export type DateExceptionFormData = z.infer<typeof dateExceptionFormSchema>;

/**
 * Schema for validating detail exception form data.
 * Ensures all values are strings for form compatibility.
 */
export const detailExceptionFormSchema = z.object({
  inServiceDate: z.string().nullable(),
  usefulLife: z.string().nullable(),
  inventoryCostBasis: z.string().nullable(),
  deployedCostBasis: z.string().nullable(),
  salvageValue: z.string().nullable(),
});

/**
 * Form data structure representing all fields that can be changed
 * when resolving detail exceptions.
 */
export type DetailExceptionFormData = z.infer<typeof detailExceptionFormSchema>;
